import { useMemo, useState, useCallback } from 'react';
import ReactFlow, { Background, Controls } from 'reactflow';
import type { Node, Edge, NodeMouseHandler } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Paper, Typography, Chip, Divider, List, ListItem, ListItemText, Tabs, Tab } from '@mui/material';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { MODULE_REGISTRY } from '@lingaai/types/graph/modules';
import { EDGE_REGISTRY, type EdgeDefinition } from '@lingaai/types/graph/edges';
import { NODE_REGISTRY } from '@lingaai/types/graph/nodes';

// PMN system-level types (metadata that can attach to any node)
const PMN_TYPES = ['Property', 'Metric', 'Narrative'];

// Universal types that can relate to anything
const UNIVERSAL_TYPES = ['Activity', 'Task', 'Document'];

// All system types (for filtering edges)
const SYSTEM_TYPES = [...PMN_TYPES];

// Package definitions
const PACKAGES = {
  business: {
    label: 'Business CRM',
    modules: ['base-crm', 'sales', 'marketing'],
  },
  pevc: {
    label: 'PE/VC CRM',
    modules: ['base-crm', 'pevc-fundraising', 'pevc-ir', 'pevc-dealflow', 'pevc-portfolio', 'marketing'],
  },
};

// Module colors
const MODULE_COLORS: Record<string, string> = {
  'base-crm': '#3b82f6',
  sales: '#8b5cf6',
  marketing: '#06b6d4',
  'pevc-fundraising': '#ec4899',
  'pevc-ir': '#f97316',
  'pevc-dealflow': '#f59e0b',
  'pevc-portfolio': '#10b981',
};

// Get module ID for a node type
const getNodeTypeModule = (nodeType: string): string | undefined => {
  for (const [moduleId, module] of Object.entries(MODULE_REGISTRY)) {
    if (module.nodeTypes.includes(nodeType)) {
      return moduleId;
    }
  }
  return undefined;
};

const getColorForType = (type: string): string => {
  const moduleId = getNodeTypeModule(type);
  return MODULE_COLORS[moduleId || ''] ?? '#6b7280';
};

interface SchemaRelationship {
  src_type: string;
  edge_type: string;
  dst_type: string;
}

// Build schema from registries (static, no DB query needed)
function buildSchemaFromRegistries(): SchemaRelationship[] {
  const relationships: SchemaRelationship[] = [];

  for (const [edgeType, edgeDef] of Object.entries(EDGE_REGISTRY) as [string, EdgeDefinition][]) {
    // Skip PMN edges and RELATED_TO (universal types handle this implicitly)
    if (['HAS_PROPERTY', 'HAS_METRIC', 'HAS_NARRATIVE', 'RELATED_TO'].includes(edgeType)) continue;

    // Skip wildcards — universal types shown separately
    if (edgeDef.sourceTypes.includes('*') || edgeDef.targetTypes.includes('*')) continue;

    const sourceTypes = edgeDef.sourceTypes.filter((t) => !SYSTEM_TYPES.includes(t) && !UNIVERSAL_TYPES.includes(t));
    const targetTypes = edgeDef.targetTypes.filter((t) => !SYSTEM_TYPES.includes(t) && !UNIVERSAL_TYPES.includes(t));

    for (const srcType of sourceTypes) {
      for (const dstType of targetTypes) {
        relationships.push({
          src_type: srcType,
          edge_type: edgeType,
          dst_type: dstType,
        });
      }
    }
  }

  return relationships;
}

export function GraphViewer() {
  const [activePackage, setActivePackage] = useState<'business' | 'pevc'>('pevc');

  // Build schema from registries (no async needed)
  const schema = useMemo(() => buildSchemaFromRegistries(), []);

  // Get node types for active package
  const packageModules = PACKAGES[activePackage].modules;
  const packageNodeTypes = useMemo(() => {
    const types = new Set<string>();
    for (const moduleId of packageModules) {
      const moduleDef = MODULE_REGISTRY[moduleId];
      if (moduleDef) {
        for (const nodeType of moduleDef.nodeTypes) {
          if (!SYSTEM_TYPES.includes(nodeType) && !UNIVERSAL_TYPES.includes(nodeType)) {
            types.add(nodeType);
          }
        }
      }
    }
    return types;
  }, [activePackage]);

  const [selectedType, setSelectedType] = useState<string | null>(null);

  const { flowNodes, flowEdges } = useMemo(() => {
    if (!schema?.length) return { flowNodes: [], flowEdges: [] };

    // Filter schema to only include relationships for this package's node types
    const filteredSchema = schema.filter((rel) => packageNodeTypes.has(rel.src_type) && packageNodeTypes.has(rel.dst_type));

    // Get connected node types for selected node
    const connectedTypes = new Set<string>();
    if (selectedType) {
      filteredSchema.forEach((rel) => {
        if (rel.src_type === selectedType) connectedTypes.add(rel.dst_type);
        if (rel.dst_type === selectedType) connectedTypes.add(rel.src_type);
      });
    }

    // Group types by module (only modules in this package)
    const moduleGroups = new Map<string, string[]>();
    for (const moduleId of packageModules) {
      const moduleDef = MODULE_REGISTRY[moduleId];
      if (moduleDef) {
        const types = moduleDef.nodeTypes.filter((t) => !SYSTEM_TYPES.includes(t) && !UNIVERSAL_TYPES.includes(t));
        if (types.length) {
          moduleGroups.set(moduleId, types);
        }
      }
    }

    const flowNodes: Node[] = [];
    let yOffset = 50;
    const nodeWidth = 180;
    const nodeGap = 100;
    const labelWidth = 160;

    // Layout by module (in package order)
    const moduleOrder = packageModules;

    moduleOrder.forEach((moduleId) => {
      const types = moduleGroups.get(moduleId);
      if (!types?.length) return;

      const moduleDef = MODULE_REGISTRY[moduleId];
      const color = MODULE_COLORS[moduleId] || '#6b7280';
      const label = moduleDef?.name || 'Other';

      // Module label node (left side)
      flowNodes.push({
        id: `module-${moduleId}`,
        position: { x: 10, y: yOffset + 12 },
        data: { label },
        style: {
          background: 'transparent',
          color: '#94a3b8',
          padding: 0,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase' as const,
          border: 'none',
          width: labelWidth,
          textAlign: 'left' as const,
        },
        selectable: false,
        draggable: false,
      });

      // Node types in this module (cleaner, minimal design)
      types.forEach((type, i) => {
        const isSelected = type === selectedType;
        const isConnected = connectedTypes.has(type);
        const isDimmed = selectedType && !isSelected && !isConnected;

        flowNodes.push({
          id: type,
          position: { x: labelWidth + 30 + i * (nodeWidth + nodeGap), y: yOffset },
          data: { label: type, nodeType: type, moduleId },
          style: {
            background: isSelected ? color : '#ffffff',
            color: isSelected ? 'white' : color,
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            border: `2px solid ${color}`,
            boxShadow: isSelected ? `0 0 0 3px ${color}20` : 'none',
            width: nodeWidth,
            textAlign: 'center' as const,
            opacity: isDimmed ? 0.25 : 1,
            transition: 'all 0.2s ease',
          },
        });
      });

      yOffset += 100;
    });

    // Add Universal row for types that can relate to anything
    flowNodes.push({
      id: 'module-universal',
      position: { x: 10, y: yOffset + 12 },
      data: { label: 'Universal' },
      style: {
        background: 'transparent',
        color: '#94a3b8',
        padding: 0,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase' as const,
        border: 'none',
        width: labelWidth,
        textAlign: 'left' as const,
      },
      selectable: false,
      draggable: false,
    });

    UNIVERSAL_TYPES.forEach((type, i) => {
      const isSelected = type === selectedType;

      flowNodes.push({
        id: type,
        position: { x: labelWidth + 30 + i * (nodeWidth + nodeGap), y: yOffset },
        data: { label: type, nodeType: type, moduleId: 'universal', isUniversal: true },
        style: {
          background: isSelected ? '#64748b' : '#ffffff',
          color: isSelected ? 'white' : '#64748b',
          padding: '10px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          border: '2px dashed #94a3b8',
          boxShadow: isSelected ? '0 0 0 3px #64748b20' : 'none',
          width: nodeWidth,
          textAlign: 'center' as const,
          opacity: 1,
          transition: 'all 0.2s ease',
        },
      });
    });

    yOffset += 100; // Standard row spacing before PMN

    // Add PMN row for metadata types
    flowNodes.push({
      id: 'module-pmn',
      position: { x: 10, y: yOffset + 12 },
      data: { label: 'PMN (Metadata)' },
      style: {
        background: 'transparent',
        color: '#94a3b8',
        padding: 0,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase' as const,
        border: 'none',
        width: labelWidth,
        textAlign: 'left' as const,
      },
      selectable: false,
      draggable: false,
    });

    PMN_TYPES.forEach((type, i) => {
      const isSelected = type === selectedType;

      flowNodes.push({
        id: type,
        position: { x: labelWidth + 30 + i * (nodeWidth + nodeGap), y: yOffset },
        data: { label: type, nodeType: type, moduleId: 'pmn', isPMN: true },
        style: {
          background: isSelected ? '#8b5cf6' : '#ffffff',
          color: isSelected ? 'white' : '#8b5cf6',
          padding: '10px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 500,
          border: '2px dotted #a78bfa',
          boxShadow: isSelected ? '0 0 0 3px #8b5cf620' : 'none',
          width: nodeWidth,
          textAlign: 'center' as const,
          opacity: 1,
          transition: 'all 0.2s ease',
        },
      });
    });

    // Show only edges connected to selected node (or all if none selected)
    const flowEdges: Edge[] = filteredSchema
      .filter((rel) => !selectedType || rel.src_type === selectedType || rel.dst_type === selectedType)
      .map((rel, index) => ({
        id: `${rel.src_type}-${rel.edge_type}-${rel.dst_type}-${index}`,
        source: rel.src_type,
        target: rel.dst_type,
        label: rel.edge_type.replace(/_/g, ' '),
        labelStyle: { fontSize: 9, fill: '#475569', fontWeight: 500 },
        labelBgStyle: { fill: 'white', fillOpacity: 0.95 },
        labelBgPadding: [6, 3] as [number, number],
        labelBgBorderRadius: 4,
        style: { stroke: '#cbd5e1', strokeWidth: 1.5 },
        type: 'smoothstep',
        zIndex: 1000,
      }));

    return { flowNodes, flowEdges };
  }, [schema, packageModules, packageNodeTypes, selectedType]);

  // Get relationships for selected type (show ALL, no package filtering)
  const selectedRelationships = useMemo(() => {
    if (!selectedType || !schema?.length) return { outgoing: [], incoming: [] };

    return {
      // Outgoing: all edges from this node (exclude system types)
      outgoing: schema.filter((rel) => rel.src_type === selectedType && !SYSTEM_TYPES.includes(rel.dst_type)),
      // Incoming: all edges to this node (exclude system types)
      incoming: schema.filter((rel) => rel.dst_type === selectedType && !SYSTEM_TYPES.includes(rel.src_type)),
    };
  }, [selectedType, schema]);

  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    if (node.id.startsWith('module-')) return;
    setSelectedType(node.data?.nodeType || null);
  }, []);

  if (!schema?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={500}>
        <Typography color="text.secondary">No schema relationships defined.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Package Tabs */}
      <Tabs
        value={activePackage}
        onChange={(_, v) => {
          setActivePackage(v);
          setSelectedType(null);
        }}
        sx={{ mb: 2 }}
      >
        <Tab value="pevc" label="PE/VC CRM" />
        <Tab value="business" label="Business CRM" />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 2, height: 550 }}>
        {/* Graph */}
        <Box
          sx={{
            flex: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            '& .react-flow__handle': {
              opacity: 0,
              pointerEvents: 'none',
            },
          }}
        >
          <ReactFlow nodes={flowNodes} edges={flowEdges} fitView nodesDraggable={true} nodesConnectable={false} elementsSelectable={true} onNodeClick={onNodeClick}>
            <Background />
            <Controls />
          </ReactFlow>
        </Box>

        {/* Detail Panel */}
        <Paper sx={{ width: 320, p: 2, overflow: 'auto' }}>
          {selectedType ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip label={selectedType} sx={{ bgcolor: getColorForType(selectedType), color: 'white', fontWeight: 600 }} />
                <Typography variant="caption" color="text.secondary">
                  {MODULE_REGISTRY[getNodeTypeModule(selectedType) || '']?.name || 'Other'}
                </Typography>
              </Box>

              {/* Node Properties */}
              {(() => {
                const nodeDef = NODE_REGISTRY[selectedType];
                if (nodeDef?.formFields && nodeDef.formFields.length > 0) {
                  return (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Properties
                      </Typography>
                      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {nodeDef.formFields.map((field) => (
                          <Box key={field.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>
                              {field.label}
                            </Typography>
                            {field.required && <Typography sx={{ color: '#ef4444', fontSize: 12 }}>*</Typography>}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                              · {field.type}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  );
                }
                return null;
              })()}

              <Divider sx={{ my: 2 }} />

              {/* Outgoing relationships */}
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ArrowRight size={14} /> Outgoing Relationships
              </Typography>
              {selectedRelationships.outgoing.length > 0 ? (
                <List dense disablePadding>
                  {selectedRelationships.outgoing.map((rel) => {
                    const edgeDef = EDGE_REGISTRY[rel.edge_type];
                    const displayName = edgeDef?.displayName || rel.edge_type.replace(/_/g, ' ');
                    return (
                      <ListItem key={`${rel.edge_type}-${rel.dst_type}`} disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}>
                              <Typography variant="body2" component="span" sx={{ color: 'primary.main' }}>
                                {displayName}
                              </Typography>
                              <ArrowRight size={12} />
                              <Chip
                                label={rel.dst_type}
                                size="small"
                                sx={{
                                  bgcolor: getColorForType(rel.dst_type),
                                  color: 'white',
                                  height: 20,
                                  fontSize: 11,
                                }}
                                onClick={() => setSelectedType(rel.dst_type)}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  None
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Incoming relationships - all shown with reverse labels */}
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ArrowLeft size={14} /> Incoming (Reverse View)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Other nodes' outgoing edges pointing here
              </Typography>
              {(() => {
                return (
                  <>
                    {selectedRelationships.incoming.length > 0 ? (
                      <List dense disablePadding>
                        {selectedRelationships.incoming.map((rel) => {
                          const edgeDef = EDGE_REGISTRY[rel.edge_type];
                          const displayName = edgeDef?.reverseDisplayName || edgeDef?.displayName || rel.edge_type.replace(/_/g, ' ');
                          return (
                            <ListItem key={`${rel.src_type}-${rel.edge_type}`} disablePadding sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}>
                                    <Chip
                                      label={rel.src_type}
                                      size="small"
                                      sx={{
                                        bgcolor: getColorForType(rel.src_type),
                                        color: 'white',
                                        height: 20,
                                        fontSize: 11,
                                      }}
                                      onClick={() => setSelectedType(rel.src_type)}
                                    />
                                    <ArrowRight size={12} />
                                    <Typography
                                      variant="body2"
                                      component="span"
                                      sx={{
                                        color: 'text.secondary',
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {displayName}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">Click a node type to see its relationships</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
